/*
   Data-fetch layer for the /admin/clients page.

   Split into two queries so pagination + search + sort of the engagements list
   round-trips only the current page, while the small "who can I add as a member"
   user roster is fetched once and reused across pages.

   Fields the wire does not carry today — flagged inline:
     - openReviewCount  → 0   (no per-status breakdown on ClientOut)
*/

import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from "@tanstack/react-query"

import { parseDetectorProfile } from "@/components/domain/detector-profile"
import { http } from "@/lib/http"
import { fromPageWire, toQueryParams, type Page, type PageWire } from "@/lib/pagination"
import { qk } from "@/lib/query-keys"
import type { TableQueryParams } from "@/lib/table"

import type { ClientId, ClientMember, ClientRow, CreateClientInput, UserId } from "../clients-data"

import { clientUsersRawQuery, toUsersById, toWireUserIdOrNull, type UserOutWire } from "./users"

// ── Wire types (mirror routes_clients.py) ──────────────────────────────────

interface ClientMemberWire {
  user_id: number
  is_primary: boolean
  created_at: string
}

interface ClientCreateWire {
  name: string
  primary_client_contact_id: number | null
  notes: string | null
}

export interface ClientOutWire {
  id: number
  name: string
  primary_client_contact_id: number | null
  notes: string | null
  is_active: boolean
  created_at: string
  members: ClientMemberWire[]
  review_count: number
  /**
   * The engagement's detector profile, Automatic included. Optional and
   * narrowed, until the generated schema carries it: a server that predates
   * the feature sends none, which reads as all Automatic.
   */
  detector_profile?: unknown
}

// ── Adapters ─────────────────────────────────────────────────────────────

function makeMemberMissingFromRoster(memberWire: ClientMemberWire): ClientMember {
  return {
    userId: String(memberWire.user_id) as UserId,
    name: `User #${String(memberWire.user_id)}`,
    email: "",
    isPrimary: memberWire.is_primary,
  }
}

function makeMember(
  m: ClientMemberWire,
  usersById: ReadonlyMap<number, UserOutWire>,
): ClientMember {
  const u = usersById.get(m.user_id)
  if (!u) return makeMemberMissingFromRoster(m)
  return {
    userId: String(u.id) as UserId,
    name: u.full_name ?? u.email,
    email: u.email,
    isPrimary: m.is_primary,
  }
}

function fromWireUserIdOrNull(wireUserId: number | null): UserId | null {
  return wireUserId === null ? null : (String(wireUserId) as UserId)
}

export function toClientRow(
  c: ClientOutWire,
  clientUsersById: ReadonlyMap<number, UserOutWire>,
): ClientRow {
  const members = c.members.map((m) => makeMember(m, clientUsersById))
  return {
    id: String(c.id) as ClientId,
    name: c.name,
    primaryClientContactId: fromWireUserIdOrNull(c.primary_client_contact_id),
    notes: c.notes,
    isActive: c.is_active,
    createdAt: c.created_at,
    members,
    reviewCount: c.review_count,
    openReviewCount: 0,
    detectorProfile: parseDetectorProfile(c.detector_profile),
  }
}

// ── The paginated engagement list. ────────────────────────────────────────

export interface ClientsPageParams {
  page: number
  pageSize: number
  search: string
  sort: { field: string; dir: "asc" | "desc" } | null
}

async function fetchClientsPage(
  qc: QueryClient,
  params: ClientsPageParams,
  signal: AbortSignal,
): Promise<Page<ClientRow>> {
  // Warm the users cache once, then build the id → user map inside the queryFn —
  // avoids a per-render Map reference change that would infinite-loop useTable's
  // fetchData dep if we passed the Map in from a `select`.
  const [clientUsers, wire] = await Promise.all([
    qc.ensureQueryData(clientUsersRawQuery()),
    http.get<PageWire<ClientOutWire>>("/api/v1/clients", {
      params: toQueryParams(
        {
          page: params.page,
          pageSize: params.pageSize,
          sort: params.sort,
          filters: {},
          search: params.search,
        } satisfies TableQueryParams,
        { include_inactive: true },
      ),
      signal,
    }),
  ])
  const clientUsersById = toUsersById(clientUsers)
  return fromPageWire(wire, (c) => toClientRow(c, clientUsersById))
}

export const clientsQuery = (qc: QueryClient, params: ClientsPageParams) =>
  queryOptions({
    queryKey: qk.clients.list(params),
    queryFn: ({ signal }) => fetchClientsPage(qc, params, signal),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })

export async function createClient(input: CreateClientInput): Promise<ClientOutWire> {
  const trimmedNotes = input.notes?.trim() ?? ""

  const body: ClientCreateWire = {
    name: input.name.trim(),
    primary_client_contact_id: toWireUserIdOrNull(input.primaryClientContactId),
    notes: trimmedNotes === "" ? null : trimmedNotes,
  }

  return http.post<ClientOutWire, ClientCreateWire>("/api/v1/clients", body)
}

export function useCreateClientMutation(): UseMutationResult<
  ClientOutWire,
  Error,
  CreateClientInput
> {
  const queryClient = useQueryClient()

  const invalidateEngagementLists = () => {
    void queryClient.invalidateQueries({ queryKey: qk.clients.all })
  }

  return useMutation({ mutationFn: createClient, onSuccess: invalidateEngagementLists })
}
